import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Search, Star, MapPin, CheckCircle, ChevronLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getInitials } from '@/lib/utils';
import { MedicoDirectorio } from './MedicoPerfilDrawer';
import { getErrorMessage } from '@/lib/errors';

interface Props {
  pacienteId?: string;
  medicoPreseleccionado?: MedicoDirectorio;
  onClose: () => void;
  onSuccess?: () => void;
}

type Paso = 'seleccionar' | 'confirmar';

function MedicoSeleccionCard({
  medico,
  seleccionado,
  onClick,
}: {
  medico: MedicoDirectorio;
  seleccionado: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
        seleccionado
          ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-300'
          : 'border-gray-100 hover:border-primary-200 hover:bg-gray-50'
      }`}
    >
      {medico.usuario?.fotoUrl ? (
        <img src={medico.usuario.fotoUrl} alt={medico.nombre} className="w-10 h-10 rounded-xl object-cover flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600 font-bold text-xs flex-shrink-0">
          {getInitials(medico.nombre)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-text-primary truncate">Dr. {medico.nombre}</p>
        {medico.especialidad && (
          <p className="text-xs text-primary-500 truncate">{medico.especialidad.nombre}</p>
        )}
        {medico.clinica && (medico.clinica.ciudad || medico.clinica.estado) && (
          <div className="flex items-center gap-1 mt-0.5">
            <MapPin size={10} className="text-gray-400 flex-shrink-0" />
            <span className="text-xs text-text-secondary truncate">
              {[medico.clinica.ciudad, medico.clinica.estado].filter(Boolean).join(', ')}
            </span>
          </div>
        )}
      </div>
      {seleccionado && <CheckCircle size={16} className="text-primary-500 flex-shrink-0" />}
    </button>
  );
}

export function ReferirPacienteModal({ pacienteId, medicoPreseleccionado, onClose, onSuccess }: Props) {
  const qc = useQueryClient();
  const [paso, setPaso] = useState<Paso>(medicoPreseleccionado ? 'confirmar' : 'seleccionar');
  const [medicoSeleccionado, setMedicoSeleccionado] = useState<MedicoDirectorio | null>(medicoPreseleccionado ?? null);
  const [q, setQ] = useState('');
  const [motivo, setMotivo] = useState('');
  const [pacienteSeleccionadoId, setPacienteSeleccionadoId] = useState(pacienteId ?? '');
  const [otorgaAcceso, setOtorgaAcceso] = useState(false);

  // Pacientes
  const { data: pacientes = [] } = useQuery({
    queryKey: ['pacientes-select'],
    queryFn: () => api.get('/api/pacientes').then(r => r.data),
  });

  // Favoritos
  const { data: favoritosData = [] } = useQuery({
    queryKey: ['red-favoritos'],
    queryFn: () => api.get('/api/red/favoritos').then(r => r.data),
  });

  // Directorio (búsqueda)
  const { data: resultados = [], isFetching } = useQuery<MedicoDirectorio[]>({
    queryKey: ['red-directorio-modal', q],
    queryFn: () =>
      api.get(`/api/red/directorio${q ? `?q=${encodeURIComponent(q)}` : ''}`).then(r => r.data),
    enabled: paso === 'seleccionar',
  });

  const favoritos: MedicoDirectorio[] = useMemo(
    () => favoritosData.map((f: any) => ({ ...f.medicoFavorito, esFavorito: true })).filter(Boolean),
    [favoritosData],
  );

  // Filtrar resultados excluyendo favoritos cuando no hay búsqueda
  const resultadosFiltrados = useMemo(() => {
    const favIds = new Set(favoritos.map(f => f.id));
    return q ? resultados : resultados.filter(r => !favIds.has(r.id));
  }, [resultados, favoritos, q]);

  const createMutation = useMutation({
    mutationFn: (dto: any) =>
      api.post('/api/referidos', { ...dto, fecha: new Date() }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['referidos'] });
      toast.success('Referido creado y enviado al médico');
      onSuccess?.();
      onClose();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear el referido')),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!medicoSeleccionado || !pacienteSeleccionadoId) {
      toast.error('Selecciona el paciente y el médico destino');
      return;
    }
    createMutation.mutate({
      pacienteId: pacienteSeleccionadoId,
      medicoDestinoId: medicoSeleccionado.id,
      motivo,
      otorgaAccesoExpediente: otorgaAcceso,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ maxHeight: '90vh' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            {paso === 'confirmar' && !medicoPreseleccionado && (
              <button
                onClick={() => { setPaso('seleccionar'); setMedicoSeleccionado(null); }}
                className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary mr-1"
              >
                <ChevronLeft size={18} />
              </button>
            )}
            <h3 className="font-semibold text-text-primary">
              {paso === 'seleccionar' ? 'Referir paciente' : 'Confirmar referido'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary">
            <X size={18} />
          </button>
        </div>

        {/* ─── PASO 1: Seleccionar médico ─────────────────────────────────── */}
        {paso === 'seleccionar' && (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Búsqueda */}
            <div className="px-5 py-3 border-b border-gray-50">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o especialidad..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
              {/* Favoritos (siempre visibles cuando no hay búsqueda) */}
              {!q && favoritos.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Star size={13} className="text-amber-400 fill-amber-400" />
                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Favoritos</span>
                  </div>
                  <div className="space-y-2">
                    {favoritos.map(m => (
                      <MedicoSeleccionCard
                        key={m.id}
                        medico={m}
                        seleccionado={medicoSeleccionado?.id === m.id}
                        onClick={() => setMedicoSeleccionado(m)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Resultados del directorio */}
              <div>
                {q && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Resultados</span>
                    {isFetching && <Loader2 size={12} className="text-gray-400 animate-spin" />}
                  </div>
                )}
                {!q && resultadosFiltrados.length > 0 && (
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Otros médicos</span>
                  </div>
                )}
                <div className="space-y-2">
                  {resultadosFiltrados.map(m => (
                    <MedicoSeleccionCard
                      key={m.id}
                      medico={m}
                      seleccionado={medicoSeleccionado?.id === m.id}
                      onClick={() => setMedicoSeleccionado(m)}
                    />
                  ))}
                  {q && !isFetching && resultadosFiltrados.length === 0 && (
                    <p className="text-sm text-text-secondary text-center py-4">Sin resultados para "{q}"</p>
                  )}
                </div>
              </div>
            </div>

            {/* Continuar */}
            <div className="flex-shrink-0 px-5 py-4 border-t border-gray-100">
              <button
                disabled={!medicoSeleccionado}
                onClick={() => setPaso('confirmar')}
                className="w-full bg-primary-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-40"
              >
                Continuar con Dr. {medicoSeleccionado?.nombre ?? '—'}
              </button>
            </div>
          </div>
        )}

        {/* ─── PASO 2: Confirmar referido ─────────────────────────────────── */}
        {paso === 'confirmar' && medicoSeleccionado && (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {/* Médico seleccionado */}
              <div className="flex items-center gap-3 bg-primary-50 rounded-xl p-3 border border-primary-100">
                <div className="w-10 h-10 rounded-xl bg-primary-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {getInitials(medicoSeleccionado.nombre)}
                </div>
                <div>
                  <p className="text-sm font-semibold text-text-primary">Dr. {medicoSeleccionado.nombre}</p>
                  {medicoSeleccionado.especialidad && (
                    <p className="text-xs text-primary-500">{medicoSeleccionado.especialidad.nombre}</p>
                  )}
                </div>
              </div>

              {/* Paciente */}
              {!pacienteId && (
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1">Paciente *</label>
                  <select
                    required
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                    value={pacienteSeleccionadoId}
                    onChange={e => setPacienteSeleccionadoId(e.target.value)}
                  >
                    <option value="">Seleccionar paciente</option>
                    {pacientes.map((p: any) => (
                      <option key={p.id} value={p.id}>{p.nombre} {p.apellidoPaterno}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Motivo */}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Motivo del referido</label>
                <textarea
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                  placeholder="Describe el motivo de la referencia..."
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                />
              </div>

              {/* Acceso expediente */}
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 w-4 h-4 rounded accent-primary-600"
                  checked={otorgaAcceso}
                  onChange={e => setOtorgaAcceso(e.target.checked)}
                />
                <div>
                  <p className="text-sm text-text-primary font-medium">Otorgar acceso al expediente</p>
                  <p className="text-xs text-text-secondary mt-0.5">
                    El médico destino podrá consultar el expediente completo del paciente
                  </p>
                </div>
              </label>

              {/* Nota informativa */}
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                <p className="text-xs text-blue-700">
                  Se notificará a <strong>Dr. {medicoSeleccionado.nombre}</strong> quien deberá aceptar la referencia antes de que sea efectiva.
                </p>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-gray-200 text-text-secondary py-2.5 rounded-xl text-sm hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createMutation.isPending || (!pacienteId && !pacienteSeleccionadoId)}
                className="flex-1 bg-primary-600 text-white py-2.5 rounded-xl text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {createMutation.isPending && <Loader2 size={14} className="animate-spin" />}
                Enviar referido
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
